import { useEffect, useState, type MouseEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSiteContent } from "@/context/SiteContentContext";

const navLinks = [
  { label: "Inicio", href: "#inicio" },
  { label: "Servicios", href: "#servicios" },
  { label: "Proyectos", href: "#proyectos" },
  { label: "Tecnologías", href: "#tecnologias" },
  { label: "Nosotros", href: "#nosotros" },
  { label: "Contacto", href: "#contacto" },
];

export default function Navbar() {
  const { settings } = useSiteContent();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const isHome = location.pathname === "/" || location.pathname === "";

  const scrollToId = (id: string) => {
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (attempts < 25) {
        attempts += 1;
        window.setTimeout(tryScroll, 120);
      }
    };
    tryScroll();
  };

  const handleNavClick = (
    event: MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    const id = href.replace("#", "");
    event.preventDefault();
    setOpen(false);
    if (isHome) {
      scrollToId(id);
    } else {
      navigate("/");
      window.setTimeout(() => scrollToId(id), 180);
    }
  };

  const brand = String(settings.brand || "MARK DIRECT COMPANY");
  const words = brand.trim().split(" ");
  const main = words.slice(0, -1).join(" ") || brand;
  const rest = words.length > 1 ? words[words.length - 1] : "";
  const logoUrl =
    typeof settings.logo_url === "string" && settings.logo_url.length > 0
      ? settings.logo_url
      : null;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "glass shadow-[0_1px_0_oklch(var(--background-200))]"
            : "bg-transparent"
        }`}
      >
        <nav className="flex items-center justify-between h-16 md:h-20 px-4 md:px-8 lg:px-12">
          <a
            href="#inicio"
            onClick={(e) => handleNavClick(e, "#inicio")}
            className="flex items-center gap-2.5 group"
            aria-label={`${brand} - Inicio`}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={brand}
                className="h-9 md:h-10 w-auto max-w-[160px] object-contain group-hover:scale-105 transition-transform"
              />
            ) : (
              <span className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 text-background-50 font-bold text-sm group-hover:scale-105 transition-transform">
                MD
              </span>
            )}
            <span className="flex flex-col leading-none">
              <span className="font-heading font-bold text-foreground-950 tracking-tight text-sm md:text-base">
                {main}
              </span>
              {rest && (
                <span className="text-[10px] md:text-xs tracking-[0.25em] text-foreground-500 font-medium">
                  {rest}
                </span>
              )}
            </span>
          </a>

          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="relative px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors group whitespace-nowrap"
              >
                {link.label}
                <span className="absolute left-4 right-4 -bottom-0.5 h-0.5 bg-primary-500 scale-x-0 group-hover:scale-x-100 transition-transform origin-left rounded-full" />
              </a>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <a
              href="#contacto"
              onClick={(e) => handleNavClick(e, "#contacto")}
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2.5 text-sm font-semibold transition-all whitespace-nowrap shadow-[0_0_20px_oklch(var(--primary-500)/0.35)]"
            >
              Crear mi proyecto
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center" />
            </a>

            <button
              type="button"
              onClick={() => setOpen(!open)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-900 hover:bg-background-100 transition-colors"
              aria-label="Abrir menú"
              aria-expanded={open}
            >
              <i className={`${open ? "ri-close-line" : "ri-menu-3-line"} text-2xl`} />
            </button>
          </div>
        </nav>
      </header>

      <div
        className={`fixed inset-0 z-40 lg:hidden transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="absolute inset-0 bg-background-950/40 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />
        <div
          className={`absolute right-0 top-0 bottom-0 w-80 max-w-[85%] bg-background-50 border-l border-background-200 flex flex-col p-6 pt-24 transition-transform duration-300 ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="flex items-center justify-between px-4 py-3.5 rounded-lg text-base font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors"
              >
                {link.label}
                <i className="ri-arrow-right-line text-foreground-400" />
              </a>
            ))}
          </div>

          <a
            href="#contacto"
            onClick={(e) => handleNavClick(e, "#contacto")}
            className="mt-auto inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-3.5 text-sm font-semibold transition-all whitespace-nowrap"
          >
            Crear mi proyecto
            <i className="ri-arrow-right-up-line" />
          </a>
        </div>
      </div>
    </>
  );
}